import React, { useState } from "react";
import { Share2, FileText, Download, Copy, Check, Trash2, X, Settings, Archive } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { axiosAuth } from "../shared/axiosAuth";
import { API } from "../data/constants";

export const SharePage = ({ shareTokens, fetchShareTokens, user }) => {
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [downloadingBackup, setDownloadingBackup] = useState(false);
  
  // Admin password reset
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetPassword, setResetPassword] = useState("");
  const [resetting, setResetting] = useState(false);

  const handleCreate = async () => {
    if (!name.trim()) { toast.error("Please enter a name"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/share`, { name, expires_days: 30 });
      toast.success("Share link created! 🔗");
      await fetchShareTokens();
      setName("");
      setShowForm(false);
    } catch (error) { toast.error("Failed to create"); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => { 
    if (!window.confirm("Delete this share link?")) return; 
    try { 
      await axios.delete(`${API}/share/${id}`); 
      toast.success("Deleted"); 
      await fetchShareTokens(); 
    } catch (error) { 
      toast.error("Failed"); 
    } 
  };

  const copyLink = (token) => {
    const link = `${window.location.origin}/shared/${token}`;
    navigator.clipboard.writeText(link);
    setCopied(token);
    toast.success("Link copied! 📋");
    setTimeout(() => setCopied(null), 2000);
  };
  
  const handleResetPassword = async () => {
    if (!resetEmail.trim() || !resetPassword.trim()) {
      toast.error("Please enter email and new password");
      return;
    }
    setResetting(true);
    try {
      await axiosAuth.post(`${API}/auth/reset-password`, { 
        email: resetEmail, 
        new_password: resetPassword 
      });
      toast.success(`Password reset for ${resetEmail}! ✅`);
      setResetEmail("");
      setResetPassword("");
      setShowResetForm(false);
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to reset password";
      toast.error(msg);
    } finally {
      setResetting(false);
    }
  };

  const downloadPDF = async (type) => {
    setDownloading(type);
    try {
      const response = await axios.get(`${API}/export/${type}`);
      const { filename, content, content_type } = response.data;
      
      // Convert base64 to blob
      const byteCharacters = atob(content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: content_type });
      
      // Download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${type === 'songbook' ? 'Songbook' : 'Journal'} PDF downloaded! 📄`);
    } catch (error) { 
      toast.error("Failed to download PDF"); 
      console.error(error);
    }
    finally { setDownloading(null); }
  };

  const downloadFullBackup = async () => {
    setDownloadingBackup(true);
    toast.info("Creating backup... This may take a minute for large collections.");
    try {
      const response = await axiosAuth.get(`${API}/backup/full`);
      const { filename, content, content_type } = response.data;
      
      // Convert base64 to blob
      const byteCharacters = atob(content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: content_type });
      
      // Download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Full backup downloaded! 💾 Your data is safe.");
    } catch (error) { 
      toast.error("Failed to create backup"); 
      console.error(error);
    }
    finally { setDownloadingBackup(false); }
  };

  return (
    <div data-testid="share-page" className="fade-in relative z-10">
      <h1 className="font-heading text-3xl md:text-4xl font-bold text-charcoal mb-4">Share with Family 👨‍👩‍👧</h1>
      <p className="font-handwritten text-2xl text-sage mb-8">Create special links or download PDFs to share with loved ones 💕</p>

      {/* PDF Export Section */}
      <div className="bg-gradient-to-br from-terracotta/10 to-sandy/10 rounded-3xl p-6 mb-8 border border-terracotta/20">
        <h2 className="font-heading text-xl font-semibold text-charcoal mb-4 flex items-center gap-2">
          <FileText size={24} className="text-terracotta" /> Download as PDF
        </h2>
        <p className="text-stone mb-4">Create beautiful PDF files to print or share digitally.</p>
        <div className="grid md:grid-cols-2 gap-4">
          <button 
            data-testid="download-songbook-pdf"
            onClick={() => downloadPDF('songbook')} 
            disabled={downloading === 'songbook'}
            className="bg-white/90 rounded-2xl p-5 border border-white/50 hover:bg-terracotta/10 transition-all flex items-center gap-4 disabled:opacity-50"
          >
            <div className="text-4xl">🎵</div>
            <div className="text-left flex-1">
              <h3 className="font-heading font-semibold text-charcoal">My Songbook</h3>
              <p className="text-stone text-sm">All songs with lyrics & stories</p>
            </div>
            <Download size={24} className={`text-terracotta ${downloading === 'songbook' ? 'animate-bounce' : ''}`} />
          </button>
          <button 
            data-testid="download-journal-pdf"
            onClick={() => downloadPDF('journal')} 
            disabled={downloading === 'journal'}
            className="bg-white/90 rounded-2xl p-5 border border-white/50 hover:bg-sage/10 transition-all flex items-center gap-4 disabled:opacity-50"
          >
            <div className="text-4xl">📔</div>
            <div className="text-left flex-1">
              <h3 className="font-heading font-semibold text-charcoal">My Pet Journal</h3>
              <p className="text-stone text-sm">Gratitude, stories & milestones</p>
            </div>
            <Download size={24} className={`text-sage ${downloading === 'journal' ? 'animate-bounce' : ''}`} />
          </button>
        </div>
      </div>

      {/* Full Backup Section (Admin Only) */}
      {user?.is_admin && (
        <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-3xl p-6 mb-8 border border-purple-200">
          <h2 className="font-heading text-xl font-semibold text-charcoal mb-4 flex items-center gap-2">
            <Archive size={24} className="text-purple-600" /> Full Data Backup
          </h2>
          <p className="text-stone mb-4">Download everything - all songs, audio files, images, pets, journals, and stories as a ZIP file. Perfect for safekeeping!</p>
          <button 
            data-testid="download-full-backup"
            onClick={downloadFullBackup} 
            disabled={downloadingBackup}
            className="bg-white/90 rounded-2xl p-5 border border-purple-200 hover:bg-purple-50 transition-all flex items-center gap-4 disabled:opacity-50 w-full md:w-auto"
          >
            <div className="text-4xl">💾</div>
            <div className="text-left flex-1">
              <h3 className="font-heading font-semibold text-charcoal">Download Full Backup</h3>
              <p className="text-stone text-sm">All data including audio & images</p>
            </div>
            <Archive size={24} className={`text-purple-600 ${downloadingBackup ? 'animate-spin' : ''}`} />
          </button>
          {downloadingBackup && (
            <p className="text-purple-600 text-sm mt-3 animate-pulse">Creating backup... This may take a minute for large collections.</p>
          )}
        </div>
      )}

      {/* Share Links Section */}
      <h2 className="font-heading text-xl font-semibold text-charcoal mb-4 flex items-center gap-2">
        <Share2 size={24} className="text-sage" /> Share Links
      </h2>
      <button data-testid="create-share-button" onClick={() => setShowForm(true)} className="w-full bg-white/90 backdrop-blur-sm rounded-3xl p-6 mb-6 border border-white/50 text-center hover:bg-sage/10 transition-all">
        <Share2 size={24} className="mx-auto text-sage mb-2" />
        <span className="text-charcoal font-medium">Create New Share Link</span>
      </button>

      {showForm && (
        <div className="fixed inset-0 bg-charcoal/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-semibold text-charcoal">Create Share Link 🔗</h2>
              <button onClick={() => setShowForm(false)} className="text-stone hover:text-charcoal p-2"><X size={24} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-charcoal font-medium mb-2">Who is this for?</label>
                <input data-testid="share-name-input" type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full h-12 border-2 border-gray-200 focus:border-sage rounded-xl px-4 outline-none" placeholder="e.g., Grandma, Pet Sitter Jane" />
              </div>
              <p className="text-stone text-sm">This link will be valid for 30 days. Anyone with this link can view your pets, songs, and moments.</p>
              <button data-testid="submit-share-button" onClick={handleCreate} disabled={loading} className="w-full bg-sage text-white py-4 rounded-full font-medium hover:bg-sage-dark transition-all disabled:opacity-50">{loading ? "Creating..." : "Create Link 🔗"}</button>
            </div>
          </div>
        </div>
      )}

      {shareTokens.length === 0 ? (
        <div className="text-center py-16 bg-white/90 backdrop-blur-sm rounded-3xl border border-white/50">
          <span className="text-5xl block mb-4">🔗</span>
          <h3 className="font-heading text-xl text-charcoal mb-2">No share links yet</h3>
          <p className="text-stone">Create a link to share your journal with family!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {shareTokens.map((t) => (
            <div key={t.id} className="bg-white/90 backdrop-blur-sm rounded-3xl p-6 shadow-sm border border-white/50">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-heading text-xl font-semibold text-charcoal">For: {t.name}</h3>
                  <p className="text-stone text-sm mt-1">Created {new Date(t.created_at).toLocaleDateString()}</p>
                  {t.expires_at && <p className="text-stone text-sm">Expires {new Date(t.expires_at).toLocaleDateString()}</p>}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => copyLink(t.token)} className="px-4 py-2 bg-sage text-white rounded-full font-medium hover:bg-sage-dark transition-all flex items-center gap-2">
                    {copied === t.token ? <Check size={18} /> : <Copy size={18} />}
                    {copied === t.token ? "Copied!" : "Copy Link"}
                  </button>
                  <button onClick={() => handleDelete(t.id)} className="p-2 text-red-500 hover:text-red-600 transition-all"><Trash2 size={20} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Admin: Password Reset Section */}
      {user?.is_admin && (
        <div className="mt-10">
          <h2 className="font-heading text-xl font-semibold text-charcoal mb-4 flex items-center gap-2">
            <Settings size={24} className="text-terracotta" /> Admin: User Management
          </h2>
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl p-6 border border-white/50">
            <button 
              onClick={() => setShowResetForm(!showResetForm)} 
              className="text-charcoal font-medium hover:text-sage transition-all flex items-center gap-2"
            >
              🔑 Reset User Password
            </button>
            
            {showResetForm && (
              <div className="mt-4 p-4 bg-gray-50 rounded-xl space-y-3">
                <div>
                  <label className="block text-charcoal font-medium mb-1 text-sm">User Email</label>
                  <input 
                    type="email" 
                    value={resetEmail} 
                    onChange={(e) => setResetEmail(e.target.value)} 
                    className="w-full h-10 border-2 border-gray-200 focus:border-sage rounded-lg px-3 outline-none text-sm" 
                    placeholder="user@example.com" 
                  />
                </div>
                <div>
                  <label className="block text-charcoal font-medium mb-1 text-sm">New Password</label>
                  <input 
                    type="text" 
                    value={resetPassword} 
                    onChange={(e) => setResetPassword(e.target.value)} 
                    className="w-full h-10 border-2 border-gray-200 focus:border-sage rounded-lg px-3 outline-none text-sm" 
                    placeholder="New password" 
                  />
                </div>
                <button 
                  onClick={handleResetPassword} 
                  disabled={resetting}
                  className="bg-terracotta text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-terracotta-dark transition-all disabled:opacity-50"
                >
                  {resetting ? "Resetting..." : "Reset Password"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SharePage;
